import { useTranslation } from 'react-i18next';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { InfoTab } from './InfoTab';
import { UsersTab } from './UsersTab';
import { EquipmentTab } from './EquipmentTab';
import { cn } from '@/lib/utils';
import { getEntityCounts, hasDuplicates, hasInvalidThresholds } from '../../utils';
import type { PreviewData, ValidationError } from '../../types';

interface PreviewEditorProps {
  data: PreviewData;
  organisationName: string;
  onChange: (data: PreviewData) => void;
  validationErrors: ValidationError[];
  onValidate: () => void;
  onExecute: () => void;
  onBack: () => void;
  isValidated: boolean;
  isValidating: boolean;
  isExecuting: boolean;
  sendWelcomeEmails: boolean;
  onSendWelcomeEmailsChange: (value: boolean) => void;
  dryRun: boolean;
  onDryRunChange: (value: boolean) => void;
}

export function PreviewEditor({
  data,
  organisationName,
  onChange,
  validationErrors,
  onValidate,
  onExecute,
  onBack,
  isValidated,
  isValidating,
  isExecuting,
  sendWelcomeEmails,
  onSendWelcomeEmailsChange,
  dryRun,
  onDryRunChange,
}: PreviewEditorProps) {
  const { t } = useTranslation('customer-onboarding');
  const counts = getEntityCounts(data.users, data.equipment);
  const duplicatesExist = hasDuplicates(data.users, data.equipment);
  const invalidThresholds = hasInvalidThresholds(data.equipment);
  const disabled = isValidating || isExecuting;
  const canExecute = isValidated && !duplicatesExist && !invalidThresholds && !disabled;

  const handleInfoChange = (info: PreviewData['info']) => {
    onChange({ ...data, info });
  };

  const handleUsersChange = (users: PreviewData['users']) => {
    onChange({ ...data, users });
  };

  const handleEquipmentChange = (equipment: PreviewData['equipment']) => {
    onChange({ ...data, equipment });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onBack}
            disabled={disabled}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('preview.back')}
          </Button>

          <div>
            <h2 className="text-lg font-semibold">{organisationName}</h2>
            <div className="flex gap-2 text-sm text-muted-foreground">
              <Badge variant="outline">{t('preview.counts.users', { count: counts.users })}</Badge>
              <Badge variant="outline">{t('preview.counts.equipment', { count: counts.equipment })}</Badge>
              <Badge variant="outline">{t('preview.counts.alertContacts', { count: counts.alertContacts })}</Badge>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isValidated && (
            <div className="flex items-center gap-1 text-sm text-green-600">
              <CheckCircle2 className="h-4 w-4" />
              <span>{t('preview.validated')}</span>
            </div>
          )}

          <Button
            type="button"
            variant="outline"
            onClick={onValidate}
            disabled={disabled}
          >
            {isValidating
              ? t('preview.validating')
              : isValidated
                ? t('preview.revalidateButton')
                : t('preview.validateButton')}
          </Button>

          <div className="flex items-center gap-2">
            <Checkbox
              id="dryRun"
              checked={dryRun}
              onCheckedChange={(checked) => onDryRunChange(!!checked)}
              disabled={disabled}
            />
            <label
              htmlFor="dryRun"
              className="text-sm text-muted-foreground cursor-pointer select-none"
            >
              {t('preview.dryRun')}
            </label>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="sendWelcomeEmails"
              checked={sendWelcomeEmails}
              onCheckedChange={(checked) => onSendWelcomeEmailsChange(!!checked)}
              disabled={disabled || dryRun}
            />
            <label
              htmlFor="sendWelcomeEmails"
              className={cn(
                'text-sm text-muted-foreground cursor-pointer select-none',
                dryRun && 'opacity-50'
              )}
            >
              {t('preview.sendWelcomeEmails')}
            </label>
          </div>

          {dryRun ? (
            <Button
              type="button"
              variant="outline"
              disabled={!canExecute}
              onClick={onExecute}
            >
              {isExecuting ? t('preview.executing') : t('preview.dryRunButton')}
            </Button>
          ) : (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  type="button"
                  disabled={!canExecute}
                >
                  {isExecuting ? t('preview.executing') : t('preview.executeButton')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('preview.confirmExecute.title')}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('preview.confirmExecute.description', { organisation: organisationName })}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('preview.confirmExecute.cancel')}</AlertDialogCancel>
                  <AlertDialogAction onClick={onExecute}>
                    {t('preview.confirmExecute.confirm')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {duplicatesExist && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          {t('preview.duplicatesWarning')}
        </div>
      )}

      <Tabs defaultValue="users" className="w-full">
        <TabsList>
          <TabsTrigger value="info">{t('preview.tabs.info')}</TabsTrigger>
          <TabsTrigger value="users">
            {t('preview.tabs.users')}
            {counts.users > 0 && (
              <Badge variant="secondary" className="ml-2">
                {counts.users}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="equipment">
            {t('preview.tabs.equipment')}
            {counts.equipment > 0 && (
              <Badge variant="secondary" className="ml-2">
                {counts.equipment}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="mt-4">
          <InfoTab
            info={data.info}
            onChange={handleInfoChange}
            disabled={disabled}
          />
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <UsersTab
            users={data.users}
            onChange={handleUsersChange}
            errors={validationErrors.filter((e) => e.entity === 'users')}
            disabled={disabled}
          />
        </TabsContent>

        <TabsContent value="equipment" className="mt-4">
          <EquipmentTab
            equipment={data.equipment}
            onChange={handleEquipmentChange}
            errors={validationErrors.filter((e) => e.entity === 'equipment')}
            disabled={disabled}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
